import { useMemo } from "react";
import PropTypes from "prop-types";
import { Label } from "@/components/ui/label";
import { RadioSegments } from "@/components/ui/radio-segments";
import { Switch } from "@/components/ui/switch";
import FieldError from "./FieldError";
import SettingsField from "./SettingsField";
import SettingsSection from "./SettingsSection";
import { EQUIPMENT_ITEMS, fieldId, UNIT_OPTIONS } from "./settingsForm";
import { formatNumber } from "../lib/format";

const NUMBERS = [
  {
    field: "dumbbell_max",
    label: "Heaviest dumbbell",
    helper: "Per dumbbell. Plans never ask for more.",
  },
  {
    field: "dumbbell_step",
    label: "Dumbbell step",
    helper: "The jump between pairs — 5 for 5, 10, 15 …",
  },
  {
    field: "barbell_step",
    label: "Barbell step",
    helper: "The smallest change on the bar — 5 with a pair of 2.5 plates.",
  },
  {
    field: "cable_step",
    label: "Cable step",
    helper: "One plate on the stack — 5, or 2.5 with half-steps.",
  },
];

/**
 * What the gym has (8 switches, each saying how many exercises need it),
 * the units the block counts in, and its steps. Sent whole when any part
 * changes. After a units switch the steps can be emptied for re-entry;
 * each empty field then shows what it was.
 */
function EquipmentSection({ equipment, settings, exercises, onChange, errorFor }) {
  const saved = settings?.equipment ?? {};
  const needs = useMemo(() => {
    const counts = {};
    for (const item of EQUIPMENT_ITEMS) {
      counts[item.code] = (exercises ?? []).filter(
        (e) => !e.archived && (e.requires ?? []).includes(item.code),
      ).length;
    }
    return counts;
  }, [exercises]);
  const set = (field, value) => onChange({ ...equipment, [field]: value });
  const toggle = (code, on) =>
    set(
      "items",
      on ? [...equipment.items.filter((c) => c !== code), code] : equipment.items.filter((c) => c !== code),
    );
  const unitsChanged = saved.units !== undefined && equipment.units !== saved.units;
  const untouchedNumbers = NUMBERS.every(
    ({ field }) => equipment[field] !== "" && Number(equipment[field]) === Number(saved[field]),
  );

  return (
    <SettingsSection
      id="training-settings-equipment"
      title="Equipment"
      error={errorFor("equipment") || undefined}
      description={
        <p>
          Plans only offer exercises whose equipment is all switched on, round loads to these
          steps, and never ask for a dumbbell past your heaviest.
        </p>
      }
    >
      <fieldset className="m-0 min-w-0 border-0 p-0">
        <legend className="mb-1.5 text-sm font-medium leading-none">What you have</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {EQUIPMENT_ITEMS.map((item) => {
            const id = `${fieldId("equipment.items")}-${item.code}`;
            const count = needs[item.code];
            return (
              <Label
                key={item.code}
                htmlFor={id}
                className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md border px-3 py-2 leading-snug font-normal hover:bg-muted md:min-h-9"
              >
                <Switch
                  id={id}
                  checked={equipment.items.includes(item.code)}
                  onCheckedChange={(on) => toggle(item.code, on)}
                  aria-labelledby={`${id}-label`}
                  aria-describedby={`${id}-detail`}
                />
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span id={`${id}-label`} className="font-medium">
                    {item.label}
                  </span>
                  <span id={`${id}-detail`} className="text-xs text-muted-foreground">
                    {item.detail ? `${item.detail} ` : ""}
                    {count === 1 ? "1 exercise needs it." : `${count} exercises need it.`}
                  </span>
                </span>
              </Label>
            );
          })}
        </div>
        <FieldError id={`${fieldId("equipment.items")}-error`}>
          {errorFor("equipment.items")}
        </FieldError>
      </fieldset>

      <div className="flex flex-col gap-1.5">
        <RadioSegments
          legend="The equipment counts in"
          options={UNIT_OPTIONS}
          value={equipment.units}
          onChange={(value) => set("units", value)}
        />
        <FieldError id={`${fieldId("equipment.units")}-error`}>
          {errorFor("equipment.units")}
        </FieldError>
        <p className="m-0 text-xs text-muted-foreground">
          {unitsChanged && untouchedNumbers
            ? `The numbers below aren't converted — re-enter them in ${equipment.units}.`
            : "Your plates and dumbbells' own units; band assist uses them too."}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {NUMBERS.map(({ field, label, helper }) => {
          const path = `equipment.${field}`;
          const was = saved[field];
          return (
            <SettingsField
              key={field}
              id={fieldId(path)}
              label={label}
              value={equipment[field]}
              onChange={(value) => set(field, value)}
              unit={equipment.units}
              helper={helper}
              placeholder={
                equipment[field] === "" && was !== undefined && was !== null
                  ? `was ${formatNumber(was)} ${saved.units}`
                  : undefined
              }
              error={errorFor(path) || undefined}
            />
          );
        })}
      </div>
    </SettingsSection>
  );
}

EquipmentSection.propTypes = {
  equipment: PropTypes.shape({
    units: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.string).isRequired,
    dumbbell_max: PropTypes.string.isRequired,
    dumbbell_step: PropTypes.string.isRequired,
    barbell_step: PropTypes.string.isRequired,
    cable_step: PropTypes.string.isRequired,
  }).isRequired,
  settings: PropTypes.object,
  exercises: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func.isRequired,
  errorFor: PropTypes.func.isRequired,
};

export default EquipmentSection;
